import { Draft, PayloadAction } from '@reduxjs/toolkit'
import {
  CheckoutPreptimeUpdatedEvent,
  CheckoutStatusUpdatedEvent,
  CheckoutStatus,
  CheckoutMetaDataItemData,
  CheckoutMetaDataType,
  CheckoutDeliveryStatusUpdatedEvent,
  OrderFormat,
  CheckoutDeliveryStatus,
  OrderTicketItemStatusUpdatedEvent,
  CheckoutCollectionTicketUpdatedEvent,
} from '@ancon/wildcat-types'

import {
  CheckoutFilters,
  CheckoutReducerState,
  CheckoutSystemProductWarningType,
  CapacityConflictOptionType,
  CheckoutRequestedItemDetails,
} from '../types'
import getCheckoutFilters from '../utils/getCheckoutFilters'
import {
  AddTipState,
  TipModalType,
} from '../../app/components/addTipModal/types'

import { checkoutInitialState } from './checkoutInitialState'

const checkoutReducers = {
  checkoutSetCheckoutFilters(
    state: Draft<CheckoutReducerState>,
    action: PayloadAction<Partial<CheckoutFilters>>,
  ) {
    const { payload } = action
    state.checkoutFilters = getCheckoutFilters(state.checkoutFilters, payload)
  },
  setSelectedCheckoutItemId(
    state: Draft<CheckoutReducerState>,
    action: PayloadAction<string | null>,
  ) {
    state.currentCheckoutSelectedCheckoutItemId = action.payload
  },
  checkoutSetIsVisibleMinimumOrderModal(
    state: Draft<CheckoutReducerState>,
    action: PayloadAction<boolean>,
  ) {
    state.isMinimumOrderModalVisible = action.payload
  },
  clearCurrentCheckout(state: Draft<CheckoutReducerState>) {
    state.currentCheckoutId = null
    state.currentCheckoutDetails = null
  },
  checkoutSetIsVisibleDiscountModal(
    state: Draft<CheckoutReducerState>,
    action: PayloadAction<boolean>,
  ) {
    state.isDiscountModalVisible = action.payload
  },
  checkoutSetTipModalVisibleType(
    state: Draft<CheckoutReducerState>,
    action: PayloadAction<TipModalType>,
  ) {
    state.checkoutTip.visibleModalType = action.payload
  },
  checkoutSetIsVisibleCustomerInfoModal(
    state: Draft<CheckoutReducerState>,
    action: PayloadAction<boolean>,
  ) {
    state.isCustomerInfoModalVisible = action.payload
  },
  checkoutSetIsVisibleDeleteOrderModal(
    state: Draft<CheckoutReducerState>,
    action: PayloadAction<boolean>,
  ) {
    state.isVisibleDeleteOrderModal = action.payload
  },
  checkoutClearCheckoutSummary(state: Draft<CheckoutReducerState>) {
    state.checkoutSummary = null
    state.checkoutSummaryOutletListItem = null
    state.checkoutSummaryFetchPending = false
    state.checkoutStatusLastUpdatedAt = 0
  },
  checkoutEmitStatusUpdatedEvent(
    state: Draft<CheckoutReducerState>,
    action: PayloadAction<CheckoutStatusUpdatedEvent>,
  ) {
    function handleCheckoutStatusEvent(
      key: 'checkoutSummary' | 'currentCheckoutDetails',
    ) {
      const {
        checkoutId,
        status,
        timestamp,
        serviceTime,
        cancelInfo,
        preparationTime,
        ticketId,
        orderId,
        orderQRInfo,
      } = action.payload

      if (state[key] && checkoutId === state[key]!.id) {
        let timestampDateValue = new Date(timestamp).valueOf()

        if (Number.isNaN(timestampDateValue)) {
          timestampDateValue = 0
        }

        if (
          !Number.isNaN(status) &&
          timestampDateValue >= state.checkoutStatusLastUpdatedAt
        ) {
          state[key]!.status = status
          state.checkoutStatusLastUpdatedAt = timestampDateValue

          if (
            status === CheckoutStatus.Captured ||
            status === CheckoutStatus.InProgress
          ) {
            // Update service time - otherwise there can be difference with POS and AOW
            state[key]!.serviceTime.time = serviceTime?.time || timestamp
          }
        }

        if (cancelInfo) {
          state[key]!.cancelInfo = cancelInfo
        }

        if (preparationTime) {
          state[key]!.tickets[0] = {
            ...state[key]!.tickets[0],
            preparationTime,
          }
        }

        if (ticketId) {
          state[key]!.tickets[0] = {
            ...state[key]!.tickets[0],
            id: ticketId,
          }
        }

        if (orderQRInfo) {
          state[key]!.orderQRInfo = orderQRInfo
        }

        /** When checkout status changing from Authorized -> Captured, orderId should be set */
        if (!!orderId && !state[key]!.orderId) {
          state[key]!.orderId = orderId
        }
      }
    }

    handleCheckoutStatusEvent('checkoutSummary')
    // Also update the current checkout details
    handleCheckoutStatusEvent('currentCheckoutDetails')
  },
  checkoutEmitPreptimeUpdatedEvent(
    state: Draft<CheckoutReducerState>,
    action: PayloadAction<CheckoutPreptimeUpdatedEvent>,
  ) {
    if (
      state.checkoutSummary &&
      action.payload.checkoutId === state.checkoutSummary.id
    ) {
      if (state.checkoutSummary.tickets[0]) {
        if (action.payload.preparationTime) {
          state.checkoutSummary.tickets[0].preparationTime =
            action.payload.preparationTime
        }
      }
    }
  },
  checkoutEmitTicketItemStatusUpdatedEvent(
    state: Draft<CheckoutReducerState>,
    action: PayloadAction<OrderTicketItemStatusUpdatedEvent>,
  ) {
    const { orderId, ticketItemId, ticketItemStatus } = action.payload

    if (state.checkoutSummary && state.checkoutSummary.orderId === orderId) {
      state.checkoutSummary.items.forEach(item => {
        const { ticketItem } = item
        if (
          ticketItem &&
          ticketItem.ticketItemId === ticketItemId &&
          ticketItem.status < ticketItemStatus
        ) {
          item.ticketItem!.status = ticketItemStatus
        }
      })
    }
  },
  checkoutEmitCollectionTicketUpdatedEvent(
    state: Draft<CheckoutReducerState>,
    action: PayloadAction<CheckoutCollectionTicketUpdatedEvent>,
  ) {
    const { checkoutId, ticketNumber } = action.payload

    if (
      state.checkoutSummary &&
      state.checkoutSummary.id === checkoutId &&
      state.checkoutSummary.ticketNumber !== ticketNumber
    ) {
      state.checkoutSummary.ticketNumber = ticketNumber
    }
  },
  checkoutUpdateCheckoutSummaryStatus(
    state: Draft<CheckoutReducerState>,
    action: PayloadAction<{ status: CheckoutStatus }>,
  ) {
    if (state.checkoutSummary) {
      state.checkoutSummary.status = action.payload.status
    }
  },
  checkoutSetIsVisibleBoxPickupScheduleOrderModal(
    state: Draft<CheckoutReducerState>,
    action: PayloadAction<boolean>,
  ) {
    state.isVisibleBoxPickupScheduleOrderModal = action.payload
  },
  /**
   * Remembers how to display items the user asked to add. The error that follows
   * only names product ids, and when the failed request was the one that would
   * have created the checkout there is nothing else to read names from.
   */
  checkoutRememberRequestedItemDetails(
    state: Draft<CheckoutReducerState>,
    action: PayloadAction<CheckoutRequestedItemDetails[]>,
  ) {
    action.payload.forEach(details => {
      state.checkoutRequestedItemDetails[details.variantId] = details
    })
  },
  checkoutHideCapacityConflictModal(state: Draft<CheckoutReducerState>) {
    state.capacityConflictModalState = null
  },
  checkoutSetCapacityConflictSelectedOption(
    state: Draft<CheckoutReducerState>,
    action: PayloadAction<CapacityConflictOptionType | null>,
  ) {
    if (state.capacityConflictModalState) {
      state.capacityConflictModalState.selectedOption = action.payload
      // Back clears the guest's work along with the selection - nothing is applied
      // until the primary CTA is pressed, so there is nothing to carry over.
      if (action.payload === null) {
        state.capacityConflictModalState.lineModes = {}
        state.capacityConflictModalState.lineQuantities = {}
      }
    }
  },
  checkoutUpdatePenguinLockersMetaData(
    state: Draft<CheckoutReducerState>,
    action: PayloadAction<Partial<CheckoutMetaDataItemData>>,
  ) {
    const { payload } = action

    if (state.currentCheckoutDetails) {
      const newMetaData = state.currentCheckoutDetails.metaData.map(
        metaData => {
          if (metaData.metaDataType === CheckoutMetaDataType.PenguinLockers) {
            return {
              ...metaData,
              metaData: {
                ...metaData.metaData,
                ...payload,
              },
            }
          }

          return metaData
        },
      )
      state.currentCheckoutDetails.metaData = newMetaData
    }
  },
  checkoutDeliveryStatusUpdatedEvent(
    state: Draft<CheckoutReducerState>,
    action: PayloadAction<CheckoutDeliveryStatusUpdatedEvent>,
  ) {
    const { checkoutId, newDeliveryStatus } = action.payload
    const { checkoutSummary } = state
    const deliveryStatus = checkoutSummary?.deliveryStatus ?? null

    if (
      checkoutSummary &&
      checkoutSummary.id === checkoutId &&
      (deliveryStatus === null || newDeliveryStatus > deliveryStatus)
    ) {
      checkoutSummary.deliveryStatus = newDeliveryStatus

      const isPickedUp = newDeliveryStatus === CheckoutDeliveryStatus.PickedUp
      const isBoxPickup = checkoutSummary.orderFormat === OrderFormat.BoxPickup

      // If the order is a box pickup and the status is picked up, set the checkout status to done
      if (isBoxPickup && isPickedUp) {
        checkoutSummary.status = CheckoutStatus.Done
      }
    }
  },
  checkoutUpdateTipState(
    state: Draft<CheckoutReducerState>,
    action: PayloadAction<Partial<AddTipState>>,
  ) {
    const { payload } = action

    state.checkoutTip = {
      ...state.checkoutTip,
      ...payload,
    }
  },
  checkoutRetryAdyenPayment(state: Draft<CheckoutReducerState>) {
    state.checkoutAdyenPaymentAttemptCount += 1
  },
  checkoutClearAdyenPaymentAttempts(state: Draft<CheckoutReducerState>) {
    state.checkoutAdyenPaymentAttemptCount = 1
  },
  checkoutSetSystemProductWarningModalVisible(
    state: Draft<CheckoutReducerState>,
    action: PayloadAction<CheckoutSystemProductWarningType>,
  ) {
    state.checkoutSystemProductWarning = action.payload
  },
  clearCheckoutState() {
    return checkoutInitialState
  },
}

export default checkoutReducers
